/**
 * Inert visual placeholder that mirrors `LaunchComposer`'s chrome
 * dimensions exactly. Rendered by `HomepagePromptCollector` while the
 * client reads localStorage + IndexedDB to restore a pending draft, for
 * signed-in users during the brief window before the redirect to `/home`
 * lands, and by both hero loading shells. Keeping the height identical to
 * the real composer is what stops the hero from jumping when the real
 * `LaunchComposer` mounts.
 *
 * Nothing here is a number anyone types twice:
 *
 * - the frame and card chrome come from `launch-composer-chrome`
 * - the editable's box comes from `prompt-input-metrics`
 *
 * One thing is still mirrored by hand, because its value is a cva +
 * tailwind-merge result rather than a literal anyone authors: the footer.
 * `PromptInputFooter` is `InputGroupAddon align="block-end"` (base `py-1.5`,
 * block-end `px-3 pb-3`) overridden with `justify-between gap-1 pb-2.5`,
 * which resolves to `px-3 pt-1.5 pb-2.5 text-sm` around a `size-8` submit
 * button.
 * That is what the footer div below spells out, and
 * `tests/hero-composer-shell-parity.test.tsx` renders both and compares the
 * resolved boxes so the derivation stays honest.
 */
import { cn } from '@brew/ui/cn'
import { PROMPT_INPUT_TEXTAREA_BOX_CLASSNAME } from '@/components/ai-elements/prompt-input-metrics'
import {
  LAUNCH_COMPOSER_FRAME_CLASSNAME,
  LAUNCH_COMPOSER_SURFACE_CLASSNAME,
} from '@/components/chat-launch/launch-composer-chrome'

export function LaunchComposerSkeleton({
  className,
  composerId = 'default',
}: {
  className?: string
  composerId?: string
}) {
  return (
    <div
      aria-hidden="true"
      className={className}
      data-testid="homepage-prompt-collector"
      data-launch-composer={composerId}
      data-launch-composer-skeleton=""
    >
      <div className={LAUNCH_COMPOSER_FRAME_CLASSNAME}>
        <div
          className={cn(
            'flex h-auto w-full flex-col border border-border bg-background dark:bg-muted',
            LAUNCH_COMPOSER_SURFACE_CLASSNAME
          )}
        >
          <div
            data-slot="composer-skeleton-textarea"
            className={PROMPT_INPUT_TEXTAREA_BOX_CLASSNAME}
          />
          <div
            data-slot="composer-skeleton-footer"
            className="flex w-full items-center justify-between gap-1 px-3 pt-1.5 pb-2.5 text-sm"
          >
            <div className="size-8" />
            <div className="size-8 shrink-0 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 opacity-50" />
          </div>
        </div>
      </div>
    </div>
  )
}
